//SendUIContainer.jsx
import React, { useState, useImperativeHandle, forwardRef } from 'react';
import PrintPreview from './PrintPreview';
import SendIt from './SendIt';

import './CSSSendUIContainer.css';

const SendUIContainer = forwardRef((props, ref) => {
    const sendModes = ['In an Email', 'To a Folder', 'To an App', 'To a Printer'];
    const [sendModeIndex, setSendModeIndex] = useState(0);
    const [secondaryModeIndex, setSecondaryModeIndex] = useState(0);
    const [sizeIndex, setSizeIndex] = useState(0);
    const [checkboxOptions, setCheckboxOptions] = useState({
        withProjectName: false,
        withCardMetadata: false,
        withCardTitle: false,
        withAnnotations: false,
        withTags: false,
        withTimestamp: false,
        withPageNumbers: false
    });

    const [tertiaryModeIndex, setTertiaryModeIndex] = useState(0);

    const handleTertiaryChange = (index) => {
        setTertiaryModeIndex(index);
    };

    const tertiaryOptions = [
        'Message',
        'Pdf',
        'Docx',
        'Xlsx',
        'Onewriter'
    ];
    const [orientationIndex, setOrientationIndex] = useState(0);
    const orientationOptions = [
        'Portrait Format',
        'Landscape Format'
    ];
    const [contentStylingIndex, setContentStylingIndex] = useState(0);
    // Add this handler function after the handleOrientationChange function
    const handleContentStylingChange = (index) => {
        setContentStylingIndex(index);
        if (index === 0) {
            showContentStyling();
        } else {
            hideContentStyling();
        }
    };
    const contentStylingOptions = [
        'Styled Content',
        'Content without Styling'
    ];

    const handleOrientationChange = (index) => {
        setOrientationIndex(index);
    };
    // State to hold scanned project data
    const [projectScanForSend, setProjectScanForSend] = useState({
        "Project Name": ""
    });

    // Size options available for all primary selections
    const sizeOptions = [
        'US Letter 8.5 inches × 11 inches\n215.9 mm × 279.4 mm',
        'A4 210 mm × 297 mm\n8.27 inches × 11.69 inches'
    ];

    // Function to initiate project scan for send - exposed to parent component
    const initiateProjectScanForSend = () => {
        console.log("initiateProjectScanForSend has been called");

        // Scan DOM for fileNameInput and store the value
        const fileNameInput = document.getElementById('fileNameInput');
        if (fileNameInput) {
            const projectName = fileNameInput.value || "";
            setProjectScanForSend(prev => ({
                ...prev,
                "Project Name": projectName
            }));
            console.log("Project name scanned:", projectName);
        } else {
            console.log("fileNameInput not found in DOM");
        }

        // Scan DOM for all <div class="cardSpacer"> elements
        const cardSpacers = document.querySelectorAll('div.cardSpacer');
        let scannedData = {};

        cardSpacers.forEach((spacer, index) => {
            const style = window.getComputedStyle(spacer);
            const isHidden = style.visibility === 'hidden' || style.display === 'none';

            if (!isHidden) {
                // Find row number
                const rowElement = spacer.querySelector('#cardOptionsTextContainerRowLocation');
                let rowMatch = null;
                if (rowElement) {
                    const rowText = rowElement.textContent;
                    const match = rowText.match(/Row:\s*(\d+)/);
                    if (match) {
                        rowMatch = match[1];
                        console.log(`Found visible cardSpacer with Row: ${rowMatch}`);
                    }
                }

                if (rowMatch) {
                    // Find first .ql-editor content
                    const qlEditor = spacer.querySelector('.ql-editor');
                    let contentHTML = '';
                    if (qlEditor) {
                        // Clone the qlEditor to process font styling
                        const clonedEditor = qlEditor.cloneNode(true);

                        // Process font styling
                        const processedHTML = processFontStyling(clonedEditor.innerHTML);
                        contentHTML = processedHTML;
                    }

                    // NEW: Scan for card title
                    let cardTitle = '';
                    const cardNameContainer = spacer.querySelector('#cardNameContainerForColumnOneInputBox');
                    if (cardNameContainer) {
                        const textareaField = cardNameContainer.querySelector('.cardNameContainerForColumnOneInputBoxField');
                        if (textareaField) {
                            const displayText = textareaField.value || textareaField.textContent || '';
                            if (displayText.trim()) {
                                cardTitle = displayText.trim();
                            } else {
                                cardTitle = textareaField.getAttribute('placeholder') || '';
                            }
                            console.log(`Card Title for Row ${rowMatch}:`, cardTitle);
                        } else {
                            console.log(`cardNameContainerForColumnOneInputBoxField not found in Row ${rowMatch}`);
                        }
                    } else {
                        console.log(`cardNameContainerForColumnOneInputBox not found in Row ${rowMatch}`);
                    }

                    // NEW: Scan for card metadata
                    let cardMetadata = '';
                    const cardPartTwo = spacer.querySelector('#cardNameContainerForColumnOnePartTwo');
                    if (cardPartTwo) {
                        const cardID = cardPartTwo.querySelector('#cardOptionsTextContainerCardID');
                        const middleText = cardPartTwo.querySelector('#cardOptionsTextContainerMiddleText');
                        const rowLocation = cardPartTwo.querySelector('#cardOptionsTextContainerRowLocation');

                        let cardIDText = cardID ? (cardID.textContent || '').trim() : '';
                        let middleTextContent = middleText ? (middleText.textContent || '').trim() : '';
                        let rowLocationText = rowLocation ? (rowLocation.textContent || '').trim() : '';

                        cardMetadata = `${cardIDText} ${middleTextContent} ${rowLocationText}`.trim();
                        console.log(`Card Metadata for Row ${rowMatch}:`, cardMetadata);
                    } else {
                        console.log(`cardNameContainerForColumnOnePartTwo not found in Row ${rowMatch}`);
                    }

                    // NEW: Scan for tags
                    let tags = '';
                    const tagsContainer = spacer.querySelector('.appliedTagsDisplayContainer');
                    if (tagsContainer) {
                        const liElements = tagsContainer.querySelectorAll('ul li');
                        if (liElements.length > 0) {
                            const tagTexts = Array.from(liElements)
                                .map(li => (li.textContent || '').trim())
                                .filter(text => text && text !== 'Apply a tag...');

                            tags = tagTexts.length > 0 ? tagTexts.join(', ') : 'No tags applied to this card';
                        } else {
                            tags = 'No tags applied to this card';
                        }
                        console.log(`Tags for Row ${rowMatch}:`, tags);
                    } else {
                        tags = 'No tags applied to this card';
                        console.log(`appliedTagsDisplayContainer not found in Row ${rowMatch}`);
                    }

                    // NEW: Scan for annotation inside .cardContainerParentForPositionOuterCol5
                    let annotationHTML = '';
                    const annotationContainer = spacer.querySelector('.cardContainerParentForPositionOuterCol5');
                    if (annotationContainer) {
                        const annotationEditor = annotationContainer.querySelector('.ql-editor');
                        if (annotationEditor && annotationEditor.innerHTML.trim()) {
                            // Clone and process font styling (just like main .ql-editor content)
                            const clonedAnnotation = annotationEditor.cloneNode(true);
                            annotationHTML = processFontStyling(clonedAnnotation.innerHTML);
                            console.log(`Annotation for Row ${rowMatch}:`, annotationHTML);
                        } else {
                            console.log(`No annotation content found in Row ${rowMatch}`);
                        }
                    } else {
                        console.log(`cardContainerParentForPositionOuterCol5 not found in Row ${rowMatch}`);
                    }


                    // Store data in scannedData object
                    scannedData[`Row ${rowMatch}`] = {
                        Row: rowMatch,
                        Content: contentHTML,
                        "Card Title": cardTitle,
                        "Card Metadata": cardMetadata,
                        "Tags": tags,
                        "Annotation": annotationHTML // <-- New nested key
                    };
                }
            }
        });

        if (Object.keys(scannedData).length > 0) {
            setProjectScanForSend(prev => ({
                ...prev,
                ...scannedData
            }));
            console.log("Final scanned project data:", scannedData);
        } else {
            console.log("No visible cardSpacers with Row data found.");
        }
    };

    // Helper function to process font styling and preserve text alignment
    const processFontStyling = (htmlString) => {
        // Create a temporary div to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;

        // Find all elements with ql-font classes
        const fontElements = tempDiv.querySelectorAll('[class*="ql-font-"]');

        fontElements.forEach(element => {
            const classList = Array.from(element.classList);
            const fontClass = classList.find(cls => cls.startsWith('ql-font-'));

            if (fontClass) {
                const fontName = fontClass.replace('ql-font-', '');
                let fontFamily = 'sans-serif'; // default

                switch (fontName) {
                    case 'serif':
                        fontFamily = 'serif';
                        break;
                    case 'monospace':
                        fontFamily = 'monospace';
                        break;
                    default:
                        fontFamily = 'sans-serif';
                }

                // Remove the ql-font class and add inline style
                element.classList.remove(fontClass);
                element.style.fontFamily = fontFamily;
            }
        });

        // Process Quill alignment classes and convert to inline styles
        const allElements = tempDiv.querySelectorAll('*');
        allElements.forEach(element => {
            const classList = Array.from(element.classList);

            // Handle text alignment classes
            const alignClass = classList.find(cls => cls.startsWith('ql-align-'));
            if (alignClass) {
                const alignment = alignClass.replace('ql-align-', '');
                element.style.textAlign = alignment;
                element.classList.remove(alignClass);
            }

            // Handle text indent classes
            const indentClass = classList.find(cls => cls.startsWith('ql-indent-'));
            if (indentClass) {
                const indentLevel = indentClass.replace('ql-indent-', '');
                const indentValue = parseInt(indentLevel) * 3; // Quill uses 3em per indent level
                element.style.paddingLeft = `${indentValue}em`;
                element.classList.remove(indentClass);
            }
        });

        // Helper function to check if an element or any ancestor has font-family styling
        const hasAncestorFontFamily = (element) => {
            let current = element;
            while (current && current !== tempDiv) {
                if (current.style && current.style.fontFamily) {
                    return true;
                }
                current = current.parentElement;
            }
            return false;
        };

        // Process text nodes that don't have font styling (apply sans-serif)
        const walker = document.createTreeWalker(
            tempDiv,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );

        const textNodes = [];
        let node;
        while (node = walker.nextNode()) {
            textNodes.push(node);
        }

        textNodes.forEach(textNode => {
            const parent = textNode.parentNode;
            // Check if parent OR ANY ANCESTOR has font-family style already applied
            if (!hasAncestorFontFamily(parent) && textNode.textContent.trim()) {
                // Wrap text node in span with sans-serif font
                const span = document.createElement('span');
                span.style.fontFamily = 'sans-serif';
                span.textContent = textNode.textContent;
                parent.replaceChild(span, textNode);
            }
        });

        return tempDiv.innerHTML;
    };

    // Expose the function to parent component via ref
    useImperativeHandle(ref, () => ({
        initiateProjectScanForSend
    }));

    // Define secondary options based on primary selection
    const getSecondaryOptions = () => {
        switch (sendModeIndex) {
            case 0: // In an Email
                return [
                    'In\nmessage content\n(with styled text)',
                    'As\nan attached .pdf\n(with styled text in spreadsheet format)',
                    'As\nan attached .Xlsx\n(with only plain text)',
                    'As\nan attached .Docx\n(with styled text)',
                    'As\nattached .Onewriter\n(complete project file)'
                ];
            case 1: // To a Folder
                return [
                    'As a\n.Txt\n(with only plain text)',
                    'As a\n.pdf\n(with styled text in spreadsheet format)',
                    'As an\n.Xlsx\n(with only plain text)',
                    'As a\n.Docx\n(with styled text)',
                    'As a\n.Onewriter\n(complete project file)'
                ];
            case 2: // To an App
                return ['Coming soon'];
            case 3: // To a Printer
                return [
                    'Print with\nstyled text',
                    'Print with\nonly plain text'
                ];
            default:
                return [];
        }
    };

    // Get the number of ghost circles to display (separate from labels)
    const getGhostCircleCount = () => {
        return sendModeIndex === 2 ? 3 : secondaryOptions.length;
    };

    // Define checkbox options based on primary and secondary selection
    const getCheckboxOptions = () => {
        return [
            { key: 'withProjectName', label: 'Project Name', inverted: false },
            { key: 'withCardTitle', label: 'Card Title', inverted: false },
            { key: 'withCardMetadata', label: 'Card Metadata', inverted: false },
            { key: 'withTags', label: 'Tags', inverted: false },
            { key: 'withAnnotations', label: 'Annotations', inverted: false },
            { key: 'withTimestamp', label: 'Timestamp', inverted: false },
            { key: 'withPageNumbers', label: 'Page Numbers', inverted: false }
        ];
    };

    const secondaryOptions = [
        'In\nDocument\nFormat',
        'In\nSpreadsheet\nFormat',
    ];
    const ghostCircleCount = getGhostCircleCount();
    const checkboxOptionsList = getCheckboxOptions();

    const handlePrimaryChange = (index) => {
        setSendModeIndex(index);
        // Don't reset secondary mode, size, or checkboxes anymore
    };

    // Reset checkboxes when secondary selection changes
    const handleSecondaryChange = (index) => {
        setSecondaryModeIndex(index);
        setCheckboxOptions({
            withProjectName: false,
            withCardTitle: false,  // ADD THIS LINE
            withCardMetadata: false,
            withAnnotations: false,
            withTags: false,
            withTimestamp: false,
            withPageNumbers: false
        });
    };

    // Handle checkbox changes
    const handleCheckboxChange = (key) => {
        setCheckboxOptions(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Handle size selection changes
    const handleSizeChange = (index) => {
        setSizeIndex(index);
    };

    const closeSendUI = () => {
        const bootstrapContainer = document.getElementById('bootstrapStarterUIContainer');
        if (bootstrapContainer) bootstrapContainer.style.display = '';

        const sendContainer = document.getElementById('sendUIContainer');
        if (sendContainer) sendContainer.style.display = 'none';

        // Clear the projectScanForSend state to reset for next use
        setProjectScanForSend({
            "Project Name": ""
        });
    };


    const hideContentStyling = () => {
        const container = document.querySelector('.print-preview-container');
        if (container) {
            container.classList.add('unstyled');
        }
    };

    const showContentStyling = () => {
        const container = document.querySelector('.print-preview-container');
        if (container) {
            container.classList.remove('unstyled');
        }
    };
    const debugProjectScanData = () => {
        console.log("=== PROJECT SCAN DEBUG DATA ===");
        console.log("Full projectScanForSend state:", projectScanForSend);
        console.log("Number of scanned rows:", Object.keys(projectScanForSend).filter(key => key.startsWith('Row')).length);

        // Log each row's data individually for better readability
        Object.entries(projectScanForSend).forEach(([key, value]) => {
            if (key.startsWith('Row')) {
                console.log(`--- ${key} ---`);
                console.log("Row Number:", value.Row);
                console.log("Card Title:", value["Card Title"]);
                console.log("Card Metadata:", value["Card Metadata"]);
                console.log("Tags:", value["Tags"]);
                console.log("Content HTML:", value.Content);
                console.log("Annotation HTML:", value.Annotation);
            } else {
                console.log(`${key}:`, value);
            }
        });
        console.log("=== END DEBUG DATA ===");
    };

    return (
        <div className="send-ui-container"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'white',
                zIndex: 9999,  // change this line
                overflowY: 'auto',
                padding: '20px'
            }}>


            <div className="send-title">OneWriter | Send</div>

            <div className="send-content-wrapper">
                {/* Left side - Controls */}
                <div className="send-controls-container">
                    {/* Primary Labels */}
                    <div className="send-label-wrapper">
                        <div className="send-toggle-labels-container">
                            {sendModes.map((label, index) => (
                                <div className="send-label-with-icon" key={label}>
                                    <span>{label}</span>
                                    <div className="send-icon-box" />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Primary Toggle Switch */}
                    <div className="send-toggle-switch">
                        {sendModes.map((mode, index) => (
                            <React.Fragment key={mode}>
                                <input
                                    type="radio"
                                    name="sendMode"
                                    id={`send-${mode}`}
                                    checked={sendModeIndex === index}
                                    onChange={() => handlePrimaryChange(index)}
                                />
                                <label htmlFor={`send-${mode}`}></label>
                            </React.Fragment>
                        ))}
                        {/* Primary Ghost Circles */}
                        {sendModes.map((_, index) => (
                            <div
                                key={`ghost-${index}`}
                                className={`send-ghost-circle ghost-${index}`}
                            />
                        ))}

                        {/* Primary circular slider */}
                        <div
                            className="send-slider"
                            style={{
                                left: ['5.5%', '35%', '65%', '94.5%'][sendModeIndex],
                                transform: 'translateX(-50%)'
                            }}
                        />
                    </div>

                    <div className="send-secondary-container">
                        {/* Secondary Horizontal Labels */}
                        <div className="send-secondary-label-wrapper">
                            <div className="send-secondary-toggle-labels-container">
                                {secondaryOptions.map((label, index) => (
                                    <div className="send-secondary-label-with-icon" key={label}>
                                        <span>{label}</span>
                                        <div className="send-icon-box" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Secondary Horizontal Toggle Switch */}
                        <div className="send-secondary-toggle-switch">
                            {secondaryOptions.map((option, index) => (
                                <React.Fragment key={`${option}-${index}`}>
                                    <input
                                        type="radio"
                                        name="secondarySendMode"
                                        id={`secondary-send-${index}`}
                                        checked={secondaryModeIndex === index}
                                        onChange={() => handleSecondaryChange(index)}
                                    />
                                    <label htmlFor={`secondary-send-${index}`}></label>
                                </React.Fragment>
                            ))}

                            {/* Secondary Ghost Circles */}
                            {secondaryOptions.map((_, index) => {
                                let leftPosition;
                                if (secondaryOptions.length === 1) {
                                    leftPosition = '50%';
                                } else {
                                    const startPercent = 5.5;
                                    const endPercent = 94.5;
                                    const range = endPercent - startPercent;
                                    leftPosition = `${startPercent + (index / (secondaryOptions.length - 1)) * range}%`;
                                }

                                return (
                                    <div
                                        key={`secondary-ghost-${index}`}
                                        className={`send-secondary-ghost-circle secondary-ghost-${index}`}
                                        style={{
                                            left: leftPosition,
                                            transform: 'translateX(-50%)'
                                        }}
                                    />
                                );
                            })}


                            {/* Secondary circular slider */}
                            <div
                                className="send-secondary-slider"
                                style={{
                                    left: secondaryOptions.length === 1
                                        ? '50%'
                                        : `${5.5 + (secondaryModeIndex / (secondaryOptions.length - 1)) * (94.5 - 5.5)}%`,
                                    transform: 'translateX(-50%)'
                                }}
                            />
                        </div>
                    </div>

                    <div className="send-tertiary-container">
                        {/* Tertiary Horizontal Labels */}
                        <div className="send-tertiary-label-wrapper">
                            <div className="send-tertiary-toggle-labels-container">
                                {tertiaryOptions.map((label, index) => (
                                    <div className="send-tertiary-label-with-icon" key={label}>
                                        <span>{label}</span>
                                        <div className="send-icon-box" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Tertiary Horizontal Toggle Switch */}
                        <div className="send-tertiary-toggle-switch">
                            {tertiaryOptions.map((option, index) => (
                                <React.Fragment key={`tertiary-${index}`}>
                                    <input
                                        type="radio"
                                        name="tertiarySendMode"
                                        id={`tertiary-send-${index}`}
                                        checked={tertiaryModeIndex === index}
                                        onChange={() => handleTertiaryChange(index)}
                                    />
                                    <label htmlFor={`tertiary-send-${index}`}></label>
                                </React.Fragment>
                            ))}

                            {/* Tertiary Ghost Circles */}
                            {tertiaryOptions.map((_, index) => {
                                let leftPosition;
                                if (tertiaryOptions.length === 1) {
                                    leftPosition = '50%';
                                } else {
                                    const startPercent = 5.5;
                                    const endPercent = 94.5;
                                    const range = endPercent - startPercent;
                                    leftPosition = `${startPercent + (index / (tertiaryOptions.length - 1)) * range}%`;
                                }

                                return (
                                    <div
                                        key={`tertiary-ghost-${index}`}
                                        className={`send-tertiary-ghost-circle tertiary-ghost-${index}`}
                                        style={{
                                            left: leftPosition,
                                            transform: 'translateX(-50%)'
                                        }}
                                    />
                                );
                            })}

                            {/* Tertiary circular slider */}
                            <div
                                className="send-tertiary-slider"
                                style={{
                                    left: tertiaryOptions.length === 1
                                        ? '50%'
                                        : `${5.5 + (tertiaryModeIndex / (tertiaryOptions.length - 1)) * (94.5 - 5.5)}%`,
                                    transform: 'translateX(-50%)'
                                }}
                            />
                        </div>
                    </div>

                    {/* Checkbox Options Section */}
                    {checkboxOptionsList.length > 0 && (
                        <div className="send-checkbox-section">
                            <div className="send-checkbox-container" style={{ flexWrap: 'wrap' }}>
                                {checkboxOptionsList.map((option) => (
                                    <label key={option.key} className="send-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={checkboxOptions[option.key]}
                                            onChange={() => handleCheckboxChange(option.key)}
                                            className="send-checkbox-input"
                                        />
                                        <span className="send-checkbox-text">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Content Styling Selection Section */}
                    {checkboxOptionsList.length > 0 && (
                        <div className="send-content-styling-section">
                            <div className="send-content-styling-label-wrapper">
                                <div className="send-content-styling-toggle-labels-container">
                                    {contentStylingOptions.map((label, index) => (
                                        <div className="send-content-styling-label-with-icon" key={label}>
                                            <span>{label}</span>
                                            <div className="send-icon-box" />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="send-content-styling-toggle-switch">
                                {contentStylingOptions.map((option, index) => (
                                    <React.Fragment key={`content-styling-${index}`}>
                                        <input
                                            type="radio"
                                            name="contentStylingMode"
                                            id={`content-styling-${index}`}
                                            checked={contentStylingIndex === index}
                                            onChange={() => handleContentStylingChange(index)}
                                        />
                                        <label htmlFor={`content-styling-${index}`}></label>
                                    </React.Fragment>
                                ))}

                                {/* Content Styling Ghost Circles */}
                                {contentStylingOptions.map((_, index) => (
                                    <div
                                        key={`content-styling-ghost-${index}`}
                                        className={`send-content-styling-ghost-circle content-styling-ghost-${index}`}
                                        style={{
                                            left: index === 0 ? '25%' : '75%',
                                            transform: 'translateX(-50%)'
                                        }}
                                    />
                                ))}

                                {/* Content Styling circular slider */}
                                <div
                                    className="send-content-styling-slider"
                                    style={{
                                        left: contentStylingIndex === 0 ? '25%' : '75%',
                                        transform: 'translateX(-50%)'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Size Selection Section - Available for all primary selections */}
                    <div className="send-size-section">
                        <div className="send-size-label-wrapper">
                            <div className="send-size-toggle-labels-container">
                                {sizeOptions.map((label, index) => (
                                    <div className="send-size-label-with-icon" key={label}>
                                        <span>{label}</span>
                                        <div className="send-icon-box" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="send-size-toggle-switch">
                            {sizeOptions.map((option, index) => (
                                <React.Fragment key={`size-${index}`}>
                                    <input
                                        type="radio"
                                        name="sizeMode"
                                        id={`size-${index}`}
                                        checked={sizeIndex === index}
                                        onChange={() => handleSizeChange(index)}
                                    />
                                    <label htmlFor={`size-${index}`}></label>
                                </React.Fragment>
                            ))}

                            {/* Size Ghost Circles */}
                            {sizeOptions.map((_, index) => (
                                <div
                                    key={`size-ghost-${index}`}
                                    className={`send-size-ghost-circle size-ghost-${index}`}
                                    style={{
                                        left: index === 0 ? '25%' : '75%',
                                        transform: 'translateX(-50%)'
                                    }}
                                />
                            ))}

                            {/* Size circular slider */}
                            <div
                                className="send-size-slider"
                                style={{
                                    left: sizeIndex === 0 ? '25%' : '75%',
                                    transform: 'translateX(-50%)'
                                }}
                            />
                        </div>
                    </div>

                    {/* Orientation Selection Section */}
                    <div className="send-orientation-section">
                        <div className="send-orientation-label-wrapper">
                            <div className="send-orientation-toggle-labels-container">
                                {orientationOptions.map((label, index) => (
                                    <div className="send-orientation-label-with-icon" key={label}>
                                        <span>{label}</span>
                                        <div className="send-icon-box" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="send-orientation-toggle-switch">
                            {orientationOptions.map((option, index) => (
                                <React.Fragment key={`orientation-${index}`}>
                                    <input
                                        type="radio"
                                        name="orientationMode"
                                        id={`orientation-${index}`}
                                        checked={orientationIndex === index}
                                        onChange={() => handleOrientationChange(index)}
                                    />
                                    <label htmlFor={`orientation-${index}`}></label>
                                </React.Fragment>
                            ))}

                            {/* Orientation Ghost Circles */}
                            {orientationOptions.map((_, index) => (
                                <div
                                    key={`orientation-ghost-${index}`}
                                    className={`send-orientation-ghost-circle orientation-ghost-${index}`}
                                    style={{
                                        left: index === 0 ? '25%' : '75%',
                                        transform: 'translateX(-50%)'
                                    }}
                                />
                            ))}

                            {/* Orientation circular slider */}
                            <div
                                className="send-orientation-slider"
                                style={{
                                    left: orientationIndex === 0 ? '25%' : '75%',
                                    transform: 'translateX(-50%)'
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Right side - Preview */}
                <div className="send-preview-container">
                    <PrintPreview
                        sizeIndex={sizeIndex}
                        checkboxOptions={checkboxOptions}
                        projectScanForSend={projectScanForSend}
                        orientationIndex={orientationIndex}
                        sendModeIndex={sendModeIndex}
                        secondaryModeIndex={secondaryModeIndex}
                    />
                </div>
            </div>

            {/* Buttons at bottom */}
            <div className="send-buttons-container">
                <SendIt />

                <button
                    id="appHeaderBarColRight-BoxLogoMenu5of5Button"
                    className="responsive-icon-button appHeaderBarColRight-BoxLogoMenu5of5Button"
                    onClick={closeSendUI}
                >
                    RETURN to Writer View
                </button>

                <button
                    className="responsive-icon-button"
                    onClick={debugProjectScanData}
                    style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'none'
                    }}
                >
                    Debug: Console Log Scanned Data
                </button>
            </div>
        </div>
    );
});

export default SendUIContainer;